import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { ChevronLeft, ChevronRight, Maximize2, Download, FileText, Loader2, RefreshCw } from "lucide-react";
import mammoth from "mammoth";
import PdfViewerModal from "./PdfViewerModal";
import DocumentReaderModal from "./DocumentReaderModal";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { fetchDocumentData, clearDocumentCache } from "../utils/documentStore";

// Configure local worker to prevent cross-origin/CORS worker security errors
if (typeof window !== "undefined" && pdfjs?.GlobalWorkerOptions) {
  // Use official CDN worker with guaranteed JavaScript MIME type matching installed pdfjs version
  const version = pdfjs.version || "5.4.296";
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
}

/**
 * InlineDocumentViewer
 * 
 * In-post, page-by-page interactive document carousel matching LinkedIn's document posts.
 * Directly renders PDF pages and DOCX content in-card with zero requirement to click/navigate away.
 */
const InlineDocumentViewer = ({ url, mediaId, filename, mimeType, sizeBytes, onRemove, uploading }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(540);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Cached document state (arrayBuffer + blobUrl)
  const [docData, setDocData] = useState(null);
  const [isFetchingDoc, setIsFetchingDoc] = useState(false);

  // DOCX inline rendering state
  const [docxPages, setDocxPages] = useState([]);
  const [docxLoading, setDocxLoading] = useState(false);

  // PDF file prop passed to react-pdf: { data: arrayBuffer } for instant in-memory rendering
  const [pdfFile, setPdfFile] = useState(null);

  // Normalise MIME type & file extension detection
  const lowerName = (filename || "").toLowerCase();
  let detectedMime = mimeType;
  if (!detectedMime || detectedMime === "application/octet-stream") {
    if (lowerName.endsWith(".pdf")) detectedMime = "application/pdf";
    else if (lowerName.endsWith(".docx")) detectedMime = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    else if (lowerName.endsWith(".doc")) detectedMime = "application/msword";
  }

  const isPdf = detectedMime === "application/pdf" || lowerName.endsWith(".pdf") || (!detectedMime && !lowerName.endsWith(".docx") && !lowerName.endsWith(".doc"));
  const isDocx = detectedMime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || lowerName.endsWith(".docx");
  const isLegacyDoc = detectedMime === "application/msword" || (lowerName.endsWith(".doc") && !lowerName.endsWith(".docx"));

  // PDF.js options: disable range/stream requests to eliminate 503 gateway drops and ensure stability
  const pdfOptions = useMemo(() => ({
    disableRange: true,
    disableStream: true,
    disableAutoFetch: true,
  }), []);

  // ── Unified Document Loading ──
  // Fetches document bytes via documentStore (Axios instance with auto JWT refresh)
  useEffect(() => {
    if (isLegacyDoc || (!mediaId && !url)) return;
    let isCancelled = false;

    const load = async () => {
      setIsFetchingDoc(true);
      setLoadError(false);

      try {
        const result = await fetchDocumentData({
          mediaId,
          url,
          mimeType: detectedMime,
        });

        if (isCancelled) return;
        setDocData(result);

        if (isPdf) {
          // Provide in-memory ArrayBuffer directly to react-pdf
          // This avoids external network fetches inside PDF.js, eliminating token expiry & range bugs
          setPdfFile(result.blobUrl || url);
          setIsFetchingDoc(false);
        } else if (isDocx) {
          setDocxLoading(true);
          try {
            const mammothResult = await mammoth.convertToHtml({ arrayBuffer: result.arrayBuffer });
            if (isCancelled) return;
            const rawHtml = mammothResult.value || "";

            // Split into logical page slides
            const parser = new DOMParser();
            const parsed = parser.parseFromString(`<div>${rawHtml}</div>`, "text/html");
            const nodes = Array.from(parsed.body.firstElementChild?.children || []);

            const pages = [];
            let currentNodes = [];
            let wordCount = 0;
            const WORDS_PER_PAGE = 260;

            const commitSlide = () => {
              if (currentNodes.length === 0) return;
              const pageWrapper = document.createElement("div");
              currentNodes.forEach((n) => pageWrapper.appendChild(n.cloneNode(true)));
              pages.push(pageWrapper.innerHTML);
              currentNodes = [];
              wordCount = 0;
            };

            nodes.forEach((el) => {
              const text = (el.textContent || "").trim();
              if (!text) return;

              const words = text.split(/\s+/).filter(Boolean).length;
              const tagName = el.tagName.toUpperCase();
              const isH1 = tagName === "H1";
              const isH2 = tagName === "H2";
              const hasBreak = el.innerHTML.includes("page-break") || tagName === "HR";

              if (text.toLowerCase().startsWith("key point:") || text.toLowerCase().startsWith("note:")) {
                el.className = "reader-callout";
                const inner = el.innerHTML;
                el.innerHTML = inner.replace(
                  /^(key point:|note:)/i,
                  '<strong class="text-pink-600 font-bold">$1</strong>'
                );
              }

              if (
                currentNodes.length > 0 &&
                (hasBreak || isH1 || (isH2 && wordCount > 60) || (wordCount + words > WORDS_PER_PAGE))
              ) {
                commitSlide();
              }

              currentNodes.push(el);
              wordCount += words;
            });

            commitSlide();

            const finalPages = pages.length > 0 ? pages : (rawHtml ? [rawHtml] : ["<p class='text-gray-400 italic'>Empty document</p>"]);
            setDocxPages(finalPages);
            setNumPages(finalPages.length);
            setPageNumber(1);
          } catch (mErr) {
            console.error("DOCX Parse Error:", mErr);
            setLoadError(true);
          } finally {
            if (!isCancelled) {
              setDocxLoading(false);
              setIsFetchingDoc(false);
            }
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("Document fetch failed:", err);
          setLoadError(true);
          setIsFetchingDoc(false);
          setDocxLoading(false);
        }
      }
    };

    load();
    return () => {
      isCancelled = true;
    };
  }, [mediaId, url, isPdf, isDocx, isLegacyDoc, detectedMime, reloadKey]);

  // Track container width to keep PDF / document scaled; debounced to prevent ResizeObserver canvas abort
  useEffect(() => {
    if (!containerRef.current) return;
    let timer = null;
    let lastWidth = Math.floor(containerRef.current.getBoundingClientRect().width);
    if (lastWidth > 0) setContainerWidth(lastWidth);

    const updateWidth = (entries) => {
      if (!entries || !entries[0]) return;
      const width = Math.floor(entries[0].contentRect.width);
      if (width <= 0) return;

      // Ignore minor changes (<= 24px) to eliminate scrollbar oscillation loops
      if (lastWidth > 0 && Math.abs(width - lastWidth) <= 24) return;

      clearTimeout(timer);
      timer = setTimeout(() => {
        lastWidth = width;
        setContainerWidth(width);
      }, 100);
    };

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setPageNumber(1);
    setLoadError(false);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    console.error("PDF Load Error:", err);
    setLoadError(true);
  }, []);

  const handleReload = useCallback(() => {
    clearDocumentCache({ mediaId, url });
    setLoadError(false);
    setReloadKey((k) => k + 1);
  }, [mediaId, url]);

  const prevPage = (e) => {
    e?.stopPropagation();
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const nextPage = (e) => {
    e?.stopPropagation();
    setPageNumber((prev) => Math.min(prev + 1, numPages || prev));
  };

  // ── Touch Swipe Navigation ──
  const touchStartRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;

    // Horizontal swipe threshold: 50px, predominantly horizontal, within 500ms
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 500) {
      if (dx < 0) nextPage();
      else prevPage();
    }
  }, [numPages, pageNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayName = filename || "Document";
  const dpr = typeof window !== "undefined" && window.devicePixelRatio ? Math.min(2, window.devicePixelRatio) : 1;
  const downloadUrl = docData?.blobUrl || url;

  return (
    <>
      <div
        ref={containerRef}
        className="group relative rounded-xl overflow-hidden border border-gray-200 bg-[#f3f2ef] my-3 select-none shadow-sm hover:border-gray-300 transition-all"
      >
        {/* ── Top Header Bar ── */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#282828] text-white z-10 relative">
          <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
            <FileText size={15} className="text-indigo-400 shrink-0" />
            <span className="text-xs font-semibold text-gray-100 truncate" title={displayName}>
              {displayName}
            </span>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            {numPages && (
              <span className="text-xs font-semibold text-gray-300 bg-white/10 px-2 py-0.5 rounded">
                {numPages} {numPages === 1 ? "page" : "pages"}
              </span>
            )}
            <div className="flex items-center gap-1 border-l border-gray-600 pl-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(true);
                }}
                className="p-1.5 rounded hover:bg-white/20 text-gray-300 hover:text-white transition-colors"
                title="Expand Reader"
              >
                <Maximize2 size={14} />
              </button>
              <a
                href={downloadUrl}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded hover:bg-white/20 text-gray-300 hover:text-white transition-colors"
                title="Download Document"
              >
                <Download size={14} />
              </a>
              {onRemove && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  className="p-1.5 rounded hover:bg-red-500/30 text-red-300 hover:text-red-100 transition-colors ml-1"
                  title="Remove Document"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Main Slide Viewport (LinkedIn Carousel) ── */}
        <div
          className="relative flex justify-center items-start min-h-[360px] bg-[#e9e8e5] py-2 px-1 sm:py-3 sm:px-2"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {uploading && (
            <div className="absolute inset-0 bg-black/50 z-30 flex flex-col items-center justify-center text-white backdrop-blur-[2px]">
              <Loader2 size={36} className="animate-spin text-indigo-400 mb-2" />
              <span className="text-xs font-semibold tracking-wide">Uploading document to post…</span>
            </div>
          )}

          {isPdf ? (
            /* ── PDF Page Canvas ── */
            loadError ? (
              <div className="p-8 text-center text-gray-600">
                <FileText size={40} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-semibold text-gray-800">Unable to display document preview</p>
                <p className="text-xs text-gray-500 mt-1">{displayName}</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleReload}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    <RefreshCw size={12} /> Reload
                  </button>
                  <a
                    href={downloadUrl}
                    download={filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    Download ({displayName})
                  </a>
                </div>
              </div>
            ) : isFetchingDoc ? (
              <div className="flex flex-col items-center justify-center p-14 text-gray-500">
                <Loader2 size={32} className="animate-spin text-indigo-600 mb-2" />
                <span className="text-xs font-medium">Fetching document…</span>
              </div>
            ) : pdfFile ? (
              <Document
                file={pdfFile}
                options={pdfOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex flex-col items-center justify-center p-14 text-gray-500">
                    <Loader2 size={32} className="animate-spin text-indigo-600 mb-2" />
                    <span className="text-xs font-medium">Rendering document…</span>
                  </div>
                }
                className="flex justify-center w-full"
              >
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth ? Math.min(containerWidth - 16, 680) : 540}
                  devicePixelRatio={dpr}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  className="shadow-md bg-white rounded-sm"
                />
              </Document>
            ) : (
              <div className="flex flex-col items-center justify-center p-14 text-gray-500">
                <Loader2 size={32} className="animate-spin text-indigo-600 mb-2" />
                <span className="text-xs font-medium">Preparing document…</span>
              </div>
            )
          ) : isDocx ? (
            /* ── DOCX Content Slide (LinkedIn Carousel) ── */
            isFetchingDoc || docxLoading ? (
              <div className="flex flex-col items-center justify-center p-14 text-gray-500">
                <Loader2 size={32} className="animate-spin text-indigo-600 mb-2" />
                <span className="text-xs font-medium">Rendering Word document…</span>
              </div>
            ) : loadError ? (
              <div className="p-8 text-center text-gray-600">
                <FileText size={40} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-semibold text-gray-800">Unable to display Word document</p>
                <p className="text-xs text-gray-500 mt-1">{displayName}</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleReload}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    <RefreshCw size={12} /> Reload
                  </button>
                  <a
                    href={downloadUrl}
                    download={filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    Download ({displayName})
                  </a>
                </div>
              </div>
            ) : (
              <div className="w-full flex justify-center">
                <div className="w-full max-w-[680px] min-h-[380px] bg-white p-4 sm:p-6 md:p-8 rounded-sm shadow-md text-gray-800 text-sm leading-relaxed document-prose border border-gray-100 overflow-hidden">
                  <div dangerouslySetInnerHTML={{ __html: docxPages[pageNumber - 1] || "" }} />
                </div>
              </div>
            )
          ) : (
            /* ── Other Files / Legacy DOC Fallback ── */
            <div className="p-8 text-center text-gray-600">
              <FileText size={40} className="mx-auto text-blue-500 mb-2" />
              <p className="text-sm font-semibold text-gray-900">{displayName}</p>
              {isLegacyDoc && <p className="text-xs text-amber-600 mt-1 font-medium">Legacy Word format (.doc) — download to view</p>}
              {sizeBytes && <p className="text-xs text-gray-400 mt-0.5">{(sizeBytes / (1024 * 1024)).toFixed(1)} MB</p>}
              <a
                href={downloadUrl}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                <Download size={14} /> Download Document
              </a>
            </div>
          )}

          {/* ── Top-Right LinkedIn Page Pill (e.g. 1/9) ── */}
          {numPages && numPages > 1 && (
            <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-xs text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-md z-20 pointer-events-none">
              {pageNumber}/{numPages}
            </div>
          )}

          {/* ── Left Navigation Arrow (<) ── */}
          {numPages && pageNumber > 1 && (
            <button
              type="button"
              onClick={prevPage}
              className="absolute left-1.5 sm:left-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-black/90 active:bg-black text-white flex items-center justify-center shadow-lg backdrop-blur-xs transition-all z-20 hover:scale-105 active:scale-95"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
            </button>
          )}

          {/* ── Right Navigation Arrow (>) ── */}
          {numPages && pageNumber < numPages && (
            <button
              type="button"
              onClick={nextPage}
              className="absolute right-1.5 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-black/90 active:bg-black text-white flex items-center justify-center shadow-lg backdrop-blur-xs transition-all z-20 hover:scale-105 active:scale-95"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
            </button>
          )}
        </div>

        {/* ── Bottom Page Progress Bar & Thumb Bar ── */}
        {numPages && numPages > 1 && (
          <div className="bg-gray-100 border-t border-gray-200 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
            <span className="font-semibold text-gray-700">
              Page {pageNumber} of {numPages}
            </span>
            <div className="flex-1 max-w-[200px] mx-4 h-1.5 bg-gray-300 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 rounded-full transition-all duration-200"
                style={{ width: `${(pageNumber / numPages) * 100}%` }}
              />
            </div>
            <div className="flex items-center gap-1 font-medium">
              <button
                type="button"
                onClick={prevPage}
                disabled={pageNumber <= 1}
                className="px-2 py-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={nextPage}
                disabled={pageNumber >= numPages}
                className="px-2 py-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Fullscreen Modal Reader (when clicking expand icon) ── */}
      {isModalOpen && (
        isPdf ? (
          <PdfViewerModal
            url={url}
            mediaId={mediaId}
            filename={displayName}
            cachedData={docData}
            initialPage={pageNumber}
            onClose={() => setIsModalOpen(false)}
          />
        ) : isDocx ? (
          <DocumentReaderModal
            url={url}
            mediaId={mediaId}
            filename={displayName}
            cachedData={docData}
            cachedSlides={docxPages}
            initialPage={pageNumber}
            onClose={() => setIsModalOpen(false)}
          />
        ) : null
      )}
    </>
  );
};

export default InlineDocumentViewer;
